import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Users,
  Clock,
  Target,
  Home as HomeIcon,
  GraduationCap,
  Binoculars,
} from "lucide-react";

const downloadBlob = async (url, params, headers, fname, mime) => {
  const res = await axios.get(url, {
    headers,
    params,
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: mime });
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const ExportsPage = () => {
  const { token, user, hasRole } = useAuth();
  const [pripravniki, setPripravniki] = useState([]);
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);

  useEffect(() => {
    const fetchPripravniki = async () => {
      try {
        const res = await axios.get(`${API}/training/pripravniki`, { headers });
        setPripravniki(res.data || []);
      } catch (e) { /* ignore */ }
    };
    fetchPripravniki();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = async (fn, label) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(`${label} izvožen`);
    } catch (e) {
      toast.error(`Napaka pri izvozu: ${label}`);
    } finally {
      setBusy(false);
    }
  };

  // ----- Members CSV -----
  const exportMembersCsv = () =>
    downloadBlob(
      `${API}/export/members-csv`,
      {},
      headers,
      `clani-${currentYear}.csv`,
      "text/csv"
    );

  const exportMembersPdf = () =>
    downloadBlob(
      `${API}/export/members-pdf`,
      {},
      headers,
      `clani-${currentYear}.pdf`,
      "application/pdf"
    );

  // ----- Hours export -----
  const exportHoursCsv = () =>
    downloadBlob(
      `${API}/export/hours-csv`,
      {},
      headers,
      `delovne-ure-${currentYear}.csv`,
      "text/csv"
    );

  const exportHoursXlsx = () =>
    downloadBlob(
      `${API}/export/hours-xlsx`,
      {},
      headers,
      `delovne-ure-${currentYear}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );

  // ----- Quotas CSV -----
  const exportQuotasCsv = () =>
    downloadBlob(
      `${API}/export/quotas-csv`,
      {},
      headers,
      `plan-odstrela-${currentYear}.csv`,
      "text/csv"
    );

  // ----- Cabin reservations (Excel split per cabin, PDF, single year or all) -----
  const exportCabin = (format, leto) =>
    downloadBlob(
      `${API}/cabins/reservations/export`,
      { format, ...(leto ? { leto } : {}) },
      headers,
      `rezervacije-koc-${leto || "vsa-leta"}.${format}`,
      format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf"
    );

  // ----- High seat reservation statistics PDF -----
  const exportReservationsPdf = (leto) =>
    downloadBlob(
      `${API}/export/reservations-pdf`,
      leto ? { leto } : {},
      headers,
      `statistika-prez-${leto || currentYear}.pdf`,
      "application/pdf"
    );

  // ----- Training plan per pripravnik -----
  const exportTraining = (uid, name, format) =>
    downloadBlob(
      `${API}/training/plan/${uid}/export`,
      { format },
      headers,
      `izobrazevanje-${(name || "").replace(/\s+/g, "-")}.${format}`,
      format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf"
    );

  // Role-based visibility
  const isManager = hasRole(["predsednik", "tajnik", "gospodar", "blagajnik"]);
  const canSeeHours = hasRole(["predsednik", "gospodar", "upravnik_lovske_koce", "skupinovodja"]);
  const canSeeCabins = hasRole(["predsednik", "gospodar", "upravnik_lovske_koce"]);
  const canSeeQuotas = hasRole(["predsednik", "gospodar"]);
  const canSeeTrainingExports =
    hasRole(["predsednik", "gospodar", "komisija_izobrazevanje", "pripravnik"]) ||
    (pripravniki && pripravniki.length > 0);

  if (!isManager && !canSeeTrainingExports) {
    return (
      <div className="space-y-4 animate-fade-in">
        <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
          <Download className="h-6 w-6" />
          Izvozi
        </h1>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Za to stran nimate dovoljenja.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exports-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <Download className="h-7 w-7" />
          Izvozi
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Centralizirana stran za izvoz vseh podatkov v Excel/PDF/CSV obliki.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Members */}
        {isManager && (
          <Card data-testid="export-section-members">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Člani
              </CardTitle>
              <CardDescription>Seznam vseh članov v CSV obliki.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={() => handle(exportMembersCsv, "Člani")}
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
              >
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                CSV – vsi člani
              </Button>
              <Button
                onClick={() => handle(exportMembersPdf, "Člani (PDF)")}
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
              >
                <FileText className="h-4 w-4 mr-2 text-red-600" />
                PDF – vsi člani
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Hours */}
        {canSeeHours && (
          <Card data-testid="export-section-hours">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Delovne ure
              </CardTitle>
              <CardDescription>Izvoz vseh evidentiranih delovnih ur.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={() => handle(exportHoursXlsx, "Delovne ure (Excel)")}
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
                data-testid="export-hours-xlsx"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                Excel — zavihki po članih + povzetek
              </Button>
              <Button
                onClick={() => handle(exportHoursCsv, "Delovne ure")}
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
              >
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                CSV — vsi zapisi
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quotas */}
        {canSeeQuotas && (
          <Card data-testid="export-section-quotas">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Plan odstrela
              </CardTitle>
              <CardDescription>Kvote po kategorijah in odstrel.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={() => handle(exportQuotasCsv, "Plan odstrela")}
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
              >
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                CSV – plan odstrela
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Cabin reservations */}
        {canSeeCabins && (
          <Card data-testid="export-section-cabins">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <HomeIcon className="h-5 w-5 text-primary" />
                Rezervacije koč
              </CardTitle>
              <CardDescription>Po koči (zavihek) za izbrano leto ali vsa leta.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" disabled={busy}>
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                    Excel po letih
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuLabel>Izberi leto</DropdownMenuLabel>
                  {yearOptions.map((y) => (
                    <DropdownMenuItem key={`xlsx-${y}`} onClick={() => handle(() => exportCabin("xlsx", y), `Koče ${y}`)}>
                      Leto {y}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handle(() => exportCabin("xlsx", null), "Koče vsa leta")}>
                    Vsa leta
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" disabled={busy}>
                    <FileText className="h-4 w-4 mr-2 text-red-600" />
                    PDF po letih
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuLabel>Izberi leto</DropdownMenuLabel>
                  {yearOptions.map((y) => (
                    <DropdownMenuItem key={`pdf-${y}`} onClick={() => handle(() => exportCabin("pdf", y), `Koče ${y}`)}>
                      Leto {y}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handle(() => exportCabin("pdf", null), "Koče vsa leta")}>
                    Vsa leta
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>
        )}

        {/* High seat reservation statistics */}
        {hasRole(["predsednik", "tajnik", "gospodar"]) && (
          <Card data-testid="export-section-reservations">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Binoculars className="h-5 w-5 text-primary" />
                Statistika prež
              </CardTitle>
              <CardDescription>Podrobna analiza rezervacij po prežah in lovcih (za sejo odbora).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" disabled={busy} data-testid="export-reservations-pdf-btn">
                    <FileText className="h-4 w-4 mr-2 text-red-600" />
                    PDF po letih
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuLabel>Izberi leto</DropdownMenuLabel>
                  {yearOptions.map((y) => (
                    <DropdownMenuItem key={`res-pdf-${y}`} onClick={() => handle(() => exportReservationsPdf(y), `Statistika prež ${y}`)} data-testid={`export-reservations-pdf-${y}`}>
                      Leto {y}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>
        )}

        {/* Training plans per pripravnik */}
        {canSeeTrainingExports && (
          <Card data-testid="export-section-training" className="sm:col-span-2 lg:col-span-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                Plan izobraževanja
                <Badge variant="outline" className="text-xs">{pripravniki.length} pripravnik(ov)</Badge>
              </CardTitle>
              <CardDescription>Napredek po pripravnikih (mentor, status, ure, podnaloge).</CardDescription>
            </CardHeader>
            <CardContent>
              {pripravniki.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ni pripravnikov za izvoz.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {pripravniki.map((p) => {
                    const fullName = `${p.ime} ${p.priimek}`;
                    return (
                      <div
                        key={p.id}
                        className="rounded-lg border p-3 flex flex-col gap-2 bg-card"
                        data-testid={`export-pripravnik-${p.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm truncate">{fullName}</span>
                          <Badge className="bg-green-100 text-green-800 text-[10px]">
                            {p.potrjeno_count || 0}/{p.skupaj || 0}
                          </Badge>
                        </div>
                        {(p.mentor_ime || p.mentor_ime_2) && (
                          <div className="text-[11px] text-muted-foreground">
                            {p.mentor_ime && <span>Mentor 1: {p.mentor_ime}</span>}
                            {p.mentor_ime_2 && (
                              <span className="block">Mentor 2: {p.mentor_ime_2}</span>
                            )}
                          </div>
                        )}
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handle(() => exportTraining(p.id, fullName, "xlsx"), fullName)}
                            disabled={busy}
                            data-testid={`export-training-${p.id}-xlsx`}
                          >
                            <FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-green-600" />
                            Excel
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handle(() => exportTraining(p.id, fullName, "pdf"), fullName)}
                            disabled={busy}
                            data-testid={`export-training-${p.id}-pdf`}
                          >
                            <FileText className="h-3.5 w-3.5 mr-1 text-red-600" />
                            PDF
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExportsPage;
